import { AssetWizardField } from '@/store/ai/assetWizard/utils/types'

import {
  ACCOUNT_MODE_FIELD,
  ACCOUNT_NAME_FIELD,
  ASSET_NAME_FIELD,
  OWNER_ACCOUNT_ID_FIELD,
} from './const'
import { WizardAccountMode, WizardFieldDef, WizardOptionsSource, WizardValues } from './types'

/**
 * The field definitions the six wizards share.
 *
 * Each wizard states its own steps and its own labels — a fund's "commitment"
 * is not a property's "purchase price" — but the control, the options source
 * and which assistant field it maps to are the same wherever the field appears.
 * Defined once here so a wizard folder holds only what is particular to it.
 */

/*
 * The ownership step's three fields. Which of them is owed depends on the mode,
 * so each states that as a predicate rather than a flat boolean — that is what
 * lets the rail count 1 outstanding field when picking an account and 2 when
 * creating one.
 *
 * `ownerAccountName` and `ownerAccountType` are filled either way: picking an
 * existing account copies its name and type in, so the summary has one value to
 * show however the account was chosen, and nothing downstream has to branch.
 * The summary states them once in a block across the top rather than on the
 * records — they are not a field of the loan or the property, and both post to
 * the same account.
 * The name is labelled Account: that is what the picker half asks for, and
 * "Name" next to the asset's own name is two names with no way to tell them
 * apart.
 */
const isCreating = (values: WizardValues) =>
  values[ACCOUNT_MODE_FIELD] === WizardAccountMode.NEW

export const ownerAccountIdField = (): WizardFieldDef => ({
  name: OWNER_ACCOUNT_ID_FIELD,
  label: 'Account',
  control: 'combobox',
  placeholder: 'Find or create new',
  required: values => !isCreating(values),
})

/*
 * Deliberately not mapped to the assistant's ACCOUNT_NAME / ACCOUNT_TYPE. Those
 * describe the account a record is *held in* — a lender, a brokerage — which is
 * what the flows that keep their own account step still collect. An owner
 * named in a sentence arrives as `ownerAccount` on the answer instead, and
 * `aiFilledFields` marks these from that — same sparkle as a stated amount,
 * without pretending they are those other two fields.
 */
export const ownerAccountNameField = (): WizardFieldDef => ({
  name: 'ownerAccountName',
  label: 'Account',
  control: 'text',
  placeholder: 'Ex. John Smith Trust',
  required: isCreating,
})

export const ownerAccountTypeField = (): WizardFieldDef => ({
  name: 'ownerAccountType',
  label: 'Type',
  control: 'combobox',
  // No options source: which types may own a record depends on the transaction
  // the wizard posts, so the ownership step resolves them and passes them in.
  placeholder: 'Find an option',
  required: isCreating,
})

export const ownerAccountFields = (): WizardFieldDef[] => [
  ownerAccountIdField(),
  ownerAccountNameField(),
  ownerAccountTypeField(),
]

export const accountNameField = (
  label: string,
  placeholder = 'Search or type a name',
  required: boolean | ((values: WizardValues) => boolean) = true,
): WizardFieldDef => ({
  name: ACCOUNT_NAME_FIELD,
  label,
  control: 'text',
  placeholder,
  required,
  aiField: AssetWizardField.ACCOUNT_NAME,
})

/**
 * The existing liability `accountName` names, when picked from a list instead
 * of typed — real_estate_with_financing's own mortgage, the other flow
 * (besides the ownership step) that offers an existing/new choice. `combobox`
 * only for the missing-fields/origin bookkeeping the ownership step's own id
 * field follows — like that one, no generic control ever renders it; the
 * step's own picker does.
 */
export const accountIdField = (
  label: string,
  required: boolean | ((values: WizardValues) => boolean),
  placeholder = 'Find an existing one',
): WizardFieldDef => ({
  name: 'accountId',
  label,
  control: 'combobox',
  placeholder,
  required,
})

export const accountTypeField = (
  options: 'positionAccountTypes' | 'liabilityAccountTypes',
): WizardFieldDef => ({
  name: 'accountType',
  label: 'Account type',
  control: 'select',
  options,
  required: true,
  aiField: AssetWizardField.ACCOUNT_TYPE,
})

export const assetNameField = (label: string, placeholder: string): WizardFieldDef => ({
  name: ASSET_NAME_FIELD,
  label,
  control: 'text',
  placeholder,
  required: true,
  aiField: AssetWizardField.ASSET_NAME,
})

/**
 * What kind of asset this is, chosen from the repository's own controlled
 * vocabulary for the transaction the flow posts — never free text. The
 * backend rejects a type outside that list, so a text field here would
 * accept a value on screen that fails the moment "Create" is pressed.
 */
export const assetTypeField = (label: string, options: WizardOptionsSource): WizardFieldDef => ({
  name: 'assetType',
  label,
  control: 'select',
  options,
  placeholder: 'Choose a type',
  aiField: AssetWizardField.ASSET_TYPE,
})

export const amountField = (label: string): WizardFieldDef => ({
  name: 'amount',
  label,
  control: 'number',
  placeholder: '0.00',
  required: true,
  aiField: AssetWizardField.AMOUNT,
})

/**
 * The date the thing happened. Defaults to the date analytics is looking at,
 * falling back to today: a user adding a property they bought last quarter is
 * usually working from the same as-of date the rest of the app is showing them,
 * and typing over a wrong default costs less than typing into an empty field.
 */
export const dateField = (label: string): WizardFieldDef => ({
  name: 'date',
  label,
  control: 'date',
  required: true,
  defaulted: 'analyticsEndDate',
  aiField: AssetWizardField.DATE,
})

/**
 * Currency is never required: an empty one resolves to the repository default,
 * which the control names in its placeholder rather than pre-selecting.
 */
export const currencyField = (): WizardFieldDef => ({
  name: 'currency',
  label: 'Currency',
  control: 'select',
  options: 'currencies',
  defaulted: 'currency',
  aiField: AssetWizardField.CURRENCY,
})

/*
 * The one field here the sentence never states outright — nothing asks "what's
 * the description?" — but it can still carry a value: whatever the sentence
 * described that no other field could hold. See AssetWizardField.NOTES.
 */
export const descriptionField = (): WizardFieldDef => ({
  name: 'description',
  label: 'Description',
  control: 'text',
  placeholder: '—',
  aiField: AssetWizardField.NOTES,
})
